import React, { useState } from 'react';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { MUCDANHGIA_API, TIEUCHI_API } from '@app/utils/apiUrl';
import { Get, Put } from '@app/utils/httpProvider';
import { messageError, messageToast, status200, styleSelectDefault } from '../../../../../utils/define';
import { URI_INTERNAL } from '../../../../../utils/pathLocations';
import { Button } from '@components';
import { AsyncPaginate } from 'react-select-async-paginate';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import { NIL as NIL_UUID } from 'uuid';

const schema = yup.object().shape({
    tieuChiId: yup.object().required(messageError.emptyMessage).nullable(),
    tenMucDanhGia: yup.string().required(messageError.emptyMessage).max(255, 'Tên mục đánh giá không được vượt quá 255 ký tự'),
    diemSo: yup.number().required(messageError.emptyMessage).min(0, 'Điểm số không được nhỏ hơn 0'),
});

const MucDanhGiaEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = require('react-router-dom').useLocation();
    const paramUrl = new URLSearchParams(location.search.substring(1));
    const id = paramUrl.get('id');

    const itemQuery = useQuery(['mucDanhGia', id], () => Get(MUCDANHGIA_API.GET_ITEM, id), {
        enabled: !!id
    });

    const editFunc = useMutation((value) =>
        Put(MUCDANHGIA_API.UPDATE_ITEM, id, value)
    );

    const handleUpdate = (values) => {
        const model = {
            tieuChiId: values.tieuChiId?.value,
            tenMucDanhGia: values.tenMucDanhGia,
            diemSo: values.diemSo
        };

        editFunc.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.invalidateQueries(['mucDanhGiaList']);
                    history.push(URI_INTERNAL.MUCDANHGIA_PATH);
                }
            },
            onError: (res) => {
                toast.error(
                    res.message || 'Đã có lỗi xảy ra, vui lòng thử lại !'
                );
            }
        });
    };

    const loadOptionsTieuChi = async (search, loadedOptions, { page }) => {
        const pageSize = 10;
        const res = await Get(TIEUCHI_API.GET_PAGING, null, {
            pageIndex: page,
            pageSize: pageSize,
            keyword: search
        });

        const items = res?.data?.$values ? res.data.$values : res?.data || [];

        return {
            options: items.map(item => ({
                value: item.id || item.Id,
                label: item.tenTieuChi || item.TenTieuChi
            })),
            hasMore: items.length >= pageSize,
            additional: {
                page: page + 1,
            },
        };
    };

    if (itemQuery.isLoading) {
        return <div>Đang tải...</div>;
    }

    if (itemQuery.isError || !itemQuery.data) {
        return <div>Có lỗi xảy ra hoặc không tìm thấy mục đánh giá</div>;
    }

    const currentData = itemQuery.data?.data ? itemQuery.data.data : itemQuery.data;

    return (
        <Card>
            <Card.Header as="h5">Cập nhật mục đánh giá</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleUpdate(values)}
                    initialValues={{
                        tieuChiId: currentData.tieuChiId && currentData.tieuChiId !== NIL_UUID
                            ? {
                                value: currentData.tieuChiId,
                                label: currentData.tieuChi?.tenTieuChi || currentData.TieuChi?.TenTieuChi || 'Đã chọn tiêu chí'
                            } : null,
                        tenMucDanhGia: currentData.tenMucDanhGia || currentData.TenMucDanhGia || '',
                        diemSo: currentData.diemSo !== undefined ? currentData.diemSo : (currentData.DiemSo !== undefined ? currentData.DiemSo : 0)
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue, setFieldTouched }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Form.Group as={Row} className="mb-3">
                                <Form.Label className="col-sm-2 col-form-label">
                                    Tiêu chí <span className="text-danger">*</span>
                                </Form.Label>
                                <Col sm="10">
                                    <AsyncPaginate
                                        value={values.tieuChiId}
                                        loadOptions={loadOptionsTieuChi}
                                        onChange={(value) => setFieldValue('tieuChiId', value)}
                                        additional={{ page: 1 }}
                                        styles={styleSelectDefault}
                                        placeholder="Chọn tiêu chí"
                                        onBlur={() => setFieldTouched('tieuChiId', true)}
                                        isClearable
                                        menuPortalTarget={document.body}
                                    />
                                    {errors.tieuChiId && (
                                        <div className="text-danger mt-1" style={{ fontSize: '13px' }}>
                                            {errors.tieuChiId}
                                        </div>
                                    )}
                                </Col>
                            </Form.Group>

                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Tên mục đánh giá',
                                        name: 'tenMucDanhGia',
                                        value: values.tenMucDanhGia,
                                        onChange: handleChange,
                                        isInvalid: errors.tenMucDanhGia,
                                        error: errors.tenMucDanhGia
                                    },
                                    {
                                        label: 'Điểm số',
                                        name: 'diemSo',
                                        type: 'number',
                                        value: values.diemSo,
                                        onChange: handleChange,
                                        isInvalid: errors.diemSo,
                                        error: errors.diemSo
                                    }
                                ]}
                            />

                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary" disabled={editFunc.isLoading}>
                                            Lưu
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.MUCDANHGIA_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
                {editFunc.isLoading && <div>Đang lưu...</div>}
            </Card.Body>
        </Card>
    );
};

export default MucDanhGiaEdit;
